import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getTeamMembers } from '../services/individualBioService';
import { TeamMember } from '../types/thematicGroups';

interface UseTeamMembersProps {
  teamUuid: string;
  page?: number;
  size?: number;
}

interface UseTeamMembersReturn {
  members: TeamMember[];
  loading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  totalPages: number;
  size: number;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
}

const useTeamMembers = ({
  teamUuid,
  page = 1,
  size = 50,
}: UseTeamMembersProps): UseTeamMembersReturn => {
  const [currentPage, setCurrentPage] = useState(page);
  const [currentSize, setCurrentSize] = useState(size);

  const { data, isLoading, error } = useQuery({
    queryKey: ['teamMembers', teamUuid, currentPage, currentSize],
    queryFn: () => getTeamMembers(teamUuid, currentPage, currentSize),
    enabled: !!teamUuid,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const setPage = (page: number) => {
    setCurrentPage(page);
  };

  const setSize = (size: number) => {
    setCurrentSize(size);
    setCurrentPage(1); // Reset para primeira página quando mudar o tamanho
  };

  return {
    members: data?.items || [],
    loading: isLoading,
    error: error?.message || null,
    total: data?.total || 0,
    currentPage,
    totalPages: data?.pages || 0,
    size: currentSize,
    setPage,
    setSize,
  };
};

export default useTeamMembers;
